import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useTemplateUsage } from '@/api/hooks/useTemplateUsage';

interface Props {
  templateId: string;
}

export function TemplateUsagePanel({ templateId }: Props) {
  const [activeOpen, setActiveOpen] = useState(false);
  const { data: usage, isLoading } = useTemplateUsage(templateId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-24" />
        </CardContent>
      </Card>
    );
  }

  if (!usage) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Assessment Usage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-border-subtle bg-bg-surface px-3 py-2">
            <p className="text-xs text-text-secondary mb-1">Active</p>
            <p className="text-xl font-semibold text-text-primary">{usage.active_count}</p>
          </div>
          <div className="rounded-md border border-border-subtle bg-bg-surface px-3 py-2">
            <p className="text-xs text-text-secondary mb-1">Historical</p>
            <p className="text-xl font-semibold text-text-primary">{usage.historical_count}</p>
          </div>
        </div>

        {usage.active_count > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setActiveOpen((o) => !o)}
              className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              {activeOpen ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              {activeOpen ? 'Hide' : 'Show'} active assessments
            </button>

            {activeOpen && (
              <div className="mt-2 space-y-1.5">
                {usage.active_assessments.map((a, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-md border border-border-subtle bg-bg-surface px-3 py-2"
                  >
                    <span className="text-xs text-text-primary truncate max-w-[60%]">{a.vendor_name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="muted" className="text-[10px] py-0">
                        {a.status.replace(/_/g, ' ')}
                      </Badge>
                      {a.due_date && (
                        <span className="text-[10px] text-text-muted font-mono">
                          {new Date(a.due_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
